import { PortCallStatus, portCallStatusFromString } from '../../../enums/portCallStatus';
import { newBerthCall, newBerthCallFromCode, newBerthCallFromCodeExcludeMovedAndDeleted } from './BerthCallFactory';
import { Port } from '../../../types/ViewModel/Port';
import { PortCall } from '../../../types/ViewModel/PortCall';
import { BerthCall } from '../../../types/ViewModel/BerthCall';
import { EventDate } from '../../../types/ViewModel/EventDate';
import { newPort, newPortFromCode, getPortName as getPortFactoryPortName } from './PortFactory';
import { PortCallBM } from '../../../types/BusinessModel/PortCallBM';
import { ReportLocationEnum } from '../../../enums/reportLocationEnum';
import { RotationTreeEnum } from '../../../enums/rotationTreeEnum';
import { MoveActionMsg } from '../../../types/ViewModel/ActionMsg';
import { createChemicalRobPortCall } from '../../TankCleaning/ChemicalRobPortCallFactory';
import { createChemicalOrderPortCallList } from '../../TankCleaning/ChemicalOrderPortCallFactory';
import { OrcaCall } from '../../../types/ViewModel/OrcaCall';

export const newSimplePortCall = (orcaCall: OrcaCall): PortCall => {
    const port = new Port(orcaCall.portId, orcaCall.portName, null, null, [], [], null);

    const portCall = new PortCall(
        orcaCall.portCallId,
        [],
        orcaCall.portCallId,
        orcaCall.voyageNumber.toString(),
        orcaCall.voyageNumber.toString(),
        null,
        orcaCall.portName,
        new EventDate(orcaCall.portArrivalDateUTC, null, orcaCall.portArrivalDateUTC, null),
        new EventDate(orcaCall.portDepartureDateUTC, null, orcaCall.portDepartureDateUTC, null),
        false,
        portCallStatusFromString(orcaCall.voyageCallStatus),
        RotationTreeEnum.PortCall,
        port,
        null,
        '',
        false,
        false,
        false,
        false,
        false,
        null,
        null,
        ReportLocationEnum.Port,
        null,
        orcaCall.portFunction,
        null,
        null,
        [],
        null,
        null,
        null,
        null
    );

    return portCall;
};

export const newPortCall = (portCallBM: PortCallBM | null, delta: number): PortCall | null => {
    if (portCallBM) {
        let voyNrPresentationStr = '';
        let voyNr1 = '';
        let voyNr2: string | null = null;
        if (portCallBM.voyageNumber.includes('/')) {
            const voyNrList = portCallBM.voyageNumber.split('/');
            if (voyNrList && voyNrList.length === 2) {
                voyNr1 = voyNrList[0];
                voyNr2 = voyNrList[1];
            }
            voyNrPresentationStr = portCallBM.voyageNumber;
        } else {
            voyNr1 = portCallBM.voyageNumber;
            voyNr2 = portCallBM.turnaroundVoyageNumber;
            voyNrPresentationStr = portCallBM.voyageNumber + (portCallBM.turnaroundVoyageNumber ? '/' + portCallBM.turnaroundVoyageNumber : '');
        }

        // Create PortCall instance first with empty berth call array
        const newPortCallInstance = new PortCall(
            portCallBM.portCallId,
            [],
            portCallBM.portCallId,
            voyNrPresentationStr,
            voyNr1,
            voyNr2,
            getPortCallNameBM(portCallBM),
            new EventDate(portCallBM.arrivalDate.plannedDateTimeUTC, null, portCallBM.arrivalDate.actualDateTimeUTC, null),
            new EventDate(portCallBM.departureDate.plannedDateTimeUTC, null, portCallBM.departureDate.actualDateTimeUTC, null),
            false,
            portCallBM.status,
            RotationTreeEnum.PortCall,
            newPort(portCallBM.port, null, delta),
            portCallBM.timeDeltaInSeconds,
            '',
            false,
            false,
            false,
            false,
            false,
            null,
            null,
            ReportLocationEnum.Port,
            portCallBM.isAlreadyIncludedInAPortCluster,
            portCallBM.imosPortFunction,
            null,
            createChemicalRobPortCall(portCallBM.chemicalRobForPortCall),
            createChemicalOrderPortCallList(
                portCallBM.chemicalOrderForPortCall?.chemicalOrderList ? portCallBM.chemicalOrderForPortCall?.chemicalOrderList : []
            ),
            null,
            null,
            null,
            null
        );

        // Now create berth calls with reference to the port call
        const berthCallArray: BerthCall[] = [];
        if (portCallBM && portCallBM.portCallItems && portCallBM.portCallItems.length > 0) {
            portCallBM.portCallItems.map((portCallItem) => {
                if (portCallItem.berthCall) {
                    const newBerthCallInstance = newBerthCall(portCallItem.berthCall, newPortCallInstance);
                    if (newBerthCallInstance) {
                        berthCallArray.push(newBerthCallInstance);
                    }
                }
            });
        }

        // Update the berth call array in the port call instance
        newPortCallInstance.berthCallArray = berthCallArray;

        newPortCallInstance.berthCallArray.forEach((b) => {
            b.portCall = newPortCallInstance;
            b.cargoOperationArray.forEach((c) => {
                c.portCall = newPortCallInstance;
            });
        });
        return newPortCallInstance;
    }
    return null;
};

export const getPortCallNameBM = (portCall: PortCallBM): string => {
    return portCall.portClusterName ? portCall.portClusterName?.toUpperCase() : portCall.port ? portCall.port.portName : '';
};

export const getPortCallName = (portCall: PortCall): string => {
    if (portCall?.port) {
        if (portCall.isInPortCluster) return portCall?.port?.portClusterName?.toUpperCase() ?? portCall?.port?.portName;
        return portCall?.port?.portName;
    }
    return '';
};

export const newPortCallFromCode = (portCall: PortCall): PortCall => {
    const berthCallArray: BerthCall[] = [];
    portCall.berthCallArray.forEach((bc) => {
        berthCallArray.push(newBerthCallFromCode(bc));
    });
    const pc = new PortCall(
        portCall.portCallId,
        berthCallArray,
        portCall.id,
        portCall.voyageNumberPresentationString,
        portCall.voyageNumber1,
        portCall.voyageNumber2,
        portCall.name,
        portCall.arrivalDate,
        portCall.departureDate,
        portCall.isSelected,
        portCall.status,
        portCall.type,
        portCall.port ? newPortFromCode(portCall.port, null, 0) : null,
        portCall.timeDeltaInSeconds,
        portCall.nodeStatus,
        portCall.isDeleted,
        portCall.isMoved,
        portCall.isMovedTo,
        portCall.isLocked,
        portCall.isInPortCluster,
        portCall.deletedIconTooltipText,
        portCall.aolDate,
        portCall.reportLocation,
        portCall.isAlreadyIncludedInAPortCluster,
        portCall.imosPortFunction,
        portCall.timezoneWarningText,
        portCall.chemicalRobForPortCall,
        portCall.chemicalOrderForPortCallList,
        portCall.portClusterInitialPortId,
        portCall.portClusterInitialPortName,
        portCall.delayInputWarningText,
        portCall.firstTimeInPortCall
    );
    pc.actionMsg = new MoveActionMsg(
        portCall.actionMsg.displayMoveIcon,
        portCall.actionMsg.message,
        portCall.actionMsg.doAction,
        portCall.actionMsg.toolTipText,
        portCall.actionMsg.moveToNodeText,
        portCall.actionMsg.id
    );

    pc.isInPortCluster = portCall.isInPortCluster;

    return pc;
};

export const newPortCallFromCodeExcludeMovedAndDeleted = (portCall: PortCall): PortCall => {
    const berthCallArray: BerthCall[] = [];
    portCall.berthCallArray.forEach((bc) => {
        if (!bc.isDeleted && !bc.isMoved) {
            berthCallArray.push(newBerthCallFromCodeExcludeMovedAndDeleted(bc));
        }
    });
    const pc = new PortCall(
        portCall.portCallId,
        berthCallArray,
        portCall.id,
        portCall.voyageNumberPresentationString,
        portCall.voyageNumber1,
        portCall.voyageNumber2,
        portCall.name,
        portCall.arrivalDate,
        portCall.departureDate,
        portCall.isSelected,
        portCall.status,
        portCall.type,
        portCall.port ? newPortFromCode(portCall.port, null, 0) : null,
        portCall.timeDeltaInSeconds,
        portCall.nodeStatus,
        portCall.isDeleted,
        portCall.isMoved,
        portCall.isMovedTo,
        portCall.isLocked,
        portCall.isInPortCluster,
        portCall.deletedIconTooltipText,
        portCall.aolDate,
        portCall.reportLocation,
        portCall.isAlreadyIncludedInAPortCluster,
        portCall.imosPortFunction,
        portCall.timezoneWarningText,
        portCall.chemicalRobForPortCall,
        portCall.chemicalOrderForPortCallList,
        portCall.portClusterInitialPortId,
        portCall.portClusterInitialPortName,
        portCall.delayInputWarningText,
        portCall.firstTimeInPortCall
    );
    pc.actionMsg = new MoveActionMsg(
        portCall.actionMsg.displayMoveIcon,
        portCall.actionMsg.message,
        portCall.actionMsg.doAction,
        portCall.actionMsg.toolTipText,
        portCall.actionMsg.moveToNodeText,
        portCall.actionMsg.id
    );

    pc.isInPortCluster = portCall.isInPortCluster;

    return pc;
};

export const newSeaPortCall = (portCall: PortCall): PortCall => {
    return new PortCall(
        portCall.portCallId,
        [],
        portCall.portCallId,
        portCall.voyageNumberPresentationString.includes('/')
            ? portCall.voyageNumberPresentationString.substring(
                  portCall.voyageNumberPresentationString.indexOf('/') + 1,
                  portCall.voyageNumberPresentationString.length
              )
            : portCall.voyageNumberPresentationString,
        portCall.voyageNumber1,
        portCall.voyageNumber2,
        getPortFactoryPortName(null, ReportLocationEnum.Sea),
        null,
        null,
        false,
        PortCallStatus.Planned,
        RotationTreeEnum.Sea,
        portCall.port,
        0,
        '',
        false,
        false,
        false,
        false,
        false,
        null,
        null,
        ReportLocationEnum.Sea,
        false,
        portCall.imosPortFunction,
        portCall.timezoneWarningText,
        null,
        [],
        null,
        null,
        null,
        null
    );
};

const getPortName = (port: Port | null, portNameFromEvent: string | null, eventTypeContextId: ReportLocationEnum): string => {
    if (port) return getPortFactoryPortName(port, eventTypeContextId);
    return portNameFromEvent ?? '???';
    // return portNameFromEvent ?? '???  (port-id: ' + port.portId.toString() + ')';
};

export const newDeletedPortCallFromCode = (
    portCallId: number | null,
    port: Port | null,
    voyageNumber: string,
    voyageNumber1: string,
    voyageNumber2: string | null,
    delta: number,
    nodeType: RotationTreeEnum,
    eventTypeContextId: ReportLocationEnum,
    portClusterInitialPortId: number | null,
    portClusterInitialPortName: string | null,
    portNameFromEvent: string | null
): PortCall => {
    if (!voyageNumber) {
        voyageNumber = '???';
    }
    return new PortCall(
        portCallId,
        [],
        portCallId,
        voyageNumber,
        voyageNumber1,
        voyageNumber2,
        getPortName(port, portNameFromEvent, eventTypeContextId),
        null,
        null,
        false,
        PortCallStatus.Planned,
        nodeType,
        newPortFromCode(port, null, delta),
        0,
        '',
        true,
        false,
        false,
        false,
        !portClusterInitialPortId || 0 ? false : true,
        null,
        null,
        eventTypeContextId,
        false,
        null,
        null,
        null,
        [],
        portClusterInitialPortId,
        portClusterInitialPortName,
        null,
        null
    );
};

export const updateBerthCallInPortCluster = (portId: number, portName: string | null, berthCallArray: BerthCall[]) => {
    berthCallArray?.forEach((bc) => {
        bc.name = (portName ? portName : '???') + ' - ' + bc.name;
        bc.portClusterInitialPortId = portId;
        bc.portClusterInitialPortName = portName;
    });
};
